import { Box, Paper, Typography } from '@mui/material';
import { colors } from '@src/styles';
import { useRunsContext } from '../context/RunsContext';
import { KpiRow, KpiTile } from './KpiTiles';
import { DailyExecutionsChart } from './DailyExecutionsChart';
import { LatencyBars } from './LatencyBars';
import { LogsTable } from './LogsTable';
import { RunsSkeleton } from './RunsSkeleton';
import { ContextSearchField } from './ContextSearchField';
import { pct, fmtDuration, card, cardTitleSX, getLogsEmptyMessage } from '../utils/format';

export function AutomationView() {
  const {
    aggregate,
    flowName,
    runs,
    error,
    isLoading,
    logsError,
    isLoadingRuns,
    logsPage,
    logsSort,
    pageSize,
    setLogsPage,
    setLogsSort,
    setSelectedRunId,
    contextSearch,
  } = useRunsContext();

  if (error)
    return (
      <Paper elevation={0} sx={card}>
        <Typography sx={{ color: colors.status.rejected, fontSize: '13.5px' }}>{error}</Typography>
      </Paper>
    );

  if (isLoading && !aggregate) return <RunsSkeleton kpiCount={3} side="latency" withLogs />;

  const item = aggregate?.items.find((x) => x.key === flowName);

  if (aggregate && !item) {
    return (
      <Paper elevation={0} sx={card}>
        <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13.5px' }}>
          Automação não encontrada no período selecionado.
        </Typography>
      </Paper>
    );
  }

  const d = item?.durationMs ?? { p50: 0, p95: 0, p99: 0, max: 0 };
  const daily = (item?.errorRateByDay ?? []).map((x) => ({
    date: x.date,
    succeeded: x.total - x.failed,
    failed: x.failed,
  }));
  return (
    <Box
      aria-busy={isLoading}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: isLoading ? 0.55 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      <KpiRow>
        <KpiTile label="Execuções" value={(item?.total ?? 0).toLocaleString('pt-BR')} />
        <KpiTile
          label="Taxa de sucesso"
          value={pct(item?.succeeded ?? 0, item?.total ?? 0)}
          tone="ok"
          sub={`${item?.failed ?? 0} falhas`}
        />
        <KpiTile
          label="Latência mediana (p50)"
          value={fmtDuration(d.p50)}
          sub={`p95 ${fmtDuration(d.p95)} · máx ${fmtDuration(d.max)}`}
        />
      </KpiRow>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.35fr 1fr',
          gap: 2,
          '@media (max-width:1050px)': { gridTemplateColumns: '1fr' },
        }}
      >
        <Paper elevation={0} sx={card}>
          <DailyExecutionsChart data={daily} />
        </Paper>
        <Paper elevation={0} sx={card}>
          <Typography component="h2" sx={cardTitleSX}>
            Distribuição de latência
          </Typography>
          <Typography sx={{ color: colors.brand.textSecondary, fontSize: '12px', mt: 0.5, mb: 1.5 }}>
            p50 = típica · p95 = 5% mais lentos · p99 = 1% pior · máx = a mais lenta
          </Typography>
          <LatencyBars p50={d.p50} p95={d.p95} p99={d.p99} max={d.max} />
        </Paper>
      </Box>
      <Paper elevation={0} sx={card}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            mb: 1.5,
            gap: 2,
          }}
        >
          <Typography component="h2" sx={cardTitleSX}>
            Execuções (logs)
          </Typography>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}>
            <ContextSearchField />
          </Box>
        </Box>
        {logsError ? (
          <Typography sx={{ color: colors.status.rejected, fontSize: '13.5px' }}>{logsError}</Typography>
        ) : (
          <LogsTable
            items={runs?.items ?? []}
            total={runs?.total ?? 0}
            page={logsPage}
            pageSize={pageSize}
            sortBy={logsSort.by}
            sortDir={logsSort.dir}
            isLoading={isLoadingRuns}
            emptyMessage={getLogsEmptyMessage(contextSearch)}
            onPageChange={setLogsPage}
            onSortChange={setLogsSort}
            onSelect={setSelectedRunId}
          />
        )}
      </Paper>
    </Box>
  );
}
